import { DeleteTaskDialog } from "@/components/Dialogs/deleteTaskDialog.component";
import { DuplicateTaskDialog } from "@/components/Dialogs/duplicateTaskDialog.component";
import TaskCellAssignee from "@/components/SingleLineTask/SingleLineTaskCells/taskCellAssignee.component";
import TaskCellDueDate from "@/components/SingleLineTask/SingleLineTaskCells/taskCellDueDate.component";
import TaskCellStatus from "@/components/SingleLineTask/SingleLineTaskCells/taskCellStatus.component";
import TaskCellTags from "@/components/SingleLineTask/SingleLineTaskCells/taskCellTags.component";
import TaskCellTitle from "@/components/SingleLineTask/SingleLineTaskCells/taskCellTitle.component";
import TaskTimer from "@/components/TaskTimer/taskTimer.component";
import { ROUTE_TASKS } from "@/constants/routes.constants";
import useDeleteTask from "@/hooks/SingleLineTask/useDeleteTask.hook";
import useDoubleClick from "@/hooks/SingleLineTask/useDoubleClick.hook";
import useDuplicateTask from "@/hooks/SingleLineTask/useDuplicateTask.hook";
import useFieldHandlers from "@/hooks/SingleLineTask/useFieldHandlers.hook";
import useHandleSave from "@/hooks/SingleLineTask/useHandleSave.hook";
import useOutsideClick from "@/hooks/SingleLineTask/useOutsideClick.hook";
import useSaveAllowed from "@/hooks/SingleLineTask/useSaveAllowed.hook";
import useSingleClick from "@/hooks/SingleLineTask/useSingleClick.hook";
import { AppContext } from "@/services/AppContext/appContext";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/shadcn/components/ui/context-menu";
import { Checkbox } from "@/shadcn/components/ui/checkbox";
import { BasicTask, EditedTask } from "@/types";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

type SingleLineTaskProps = {
  task: BasicTask;
  completeTask: (taskID: number) => void;
  taskType: "Overdue" | "Due Today" | "Due Later" | "No Due Date";
  inlineEditingDisabled?: boolean;
  bulkEditVersion?: number;
  isSelected?: boolean;
  onSelectChange?: (selected: boolean) => void;
};

const SingleLineTask: React.FC<SingleLineTaskProps> = ({
  task,
  completeTask,
  taskType,
  inlineEditingDisabled = false,
  bulkEditVersion = 0,
  isSelected = false,
  onSelectChange,
}) => {
  const nav = useNavigate();
  const rowRef = useRef<HTMLDivElement>(null);
  const { forceFetchAll, removeTaskFromUserTasks } = useContext(AppContext);

  const [clickTimeout, setClickTimeout] = useState<NodeJS.Timeout | null>(null);
  // When a task is clicked in all task view, use to compare against edited task
  const [selectedEditableTask, setSelectedEditableTask] =
    useState<EditedTask | null>(null);
  // Task being edited
  const [editedTask, setEditedTask] = useState<EditedTask | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const saveAllowed = useRef<boolean>(false);
  const createTagOpen = useRef<boolean>(false);
  const createDueDateOpen = useRef<boolean>(false);
  const createAssigneeOpen = useRef<boolean>(false);

  // Dialogs
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // #region Edit Task Fields
  const editedTaskRef = useRef(editedTask);
  // Update edited task across renders
  useEffect(() => {
    editedTaskRef.current = editedTask;
  }, [editedTask]);

  useEffect(() => {
    if (!inlineEditingDisabled) {
      return;
    }

    const hadUnsavedChanges = saveAllowed.current;

    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
    }

    setIsEditing(false);
    if (rowRef.current) {
      rowRef.current.style.outline = "";
    }

    if (hadUnsavedChanges) {
      setEditedTask(selectedEditableTask ?? null);
    }

    saveAllowed.current = false;
  }, [
    clickTimeout,
    inlineEditingDisabled,
    selectedEditableTask,
    setClickTimeout,
    saveAllowed,
  ]);

  useEffect(() => {
    if (bulkEditVersion === 0) {
      return;
    }

    setClickTimeout((prevTimeout) => {
      if (prevTimeout) {
        clearTimeout(prevTimeout);
      }
      return null;
    });

    setIsEditing(false);
    setEditedTask(null);
    setSelectedEditableTask(null);
    saveAllowed.current = false;

    if (rowRef.current) {
      rowRef.current.style.outline = "";
    }
  }, [bulkEditVersion, setClickTimeout]);

  const {
    handleFieldAssignedTo,
    handleEditField,
    handleFieldDueDate,
    handleFieldTags,
  } = useFieldHandlers(editedTask, setEditedTask);

  // Sends updated task to the server
  const { handleSave } = useHandleSave(
    editedTask,
    "UserTasks",
    saveAllowed,
    editedTaskRef,
    setIsEditing,
    setSelectedEditableTask
  );

  // #endregion Edit Task

  // #region Lifecycle
  // Checks if save is allowed: Something has changed and the title is not empty
  useSaveAllowed(editedTask, selectedEditableTask, saveAllowed);

  // #endregion Lifecycle

  // #region Click Handling Edit/View
  // Single click action (Editing)
  const { handleSingleClick } = useSingleClick(
    task,
    isEditing,
    selectedEditableTask,
    editedTask,
    setIsEditing,
    setSelectedEditableTask,
    setEditedTask,
    rowRef,
    clickTimeout,
    setClickTimeout
  );

  // Double click action (Open Task Details)
  const { handleDoubleClick } = useDoubleClick(
    task.id,
    clickTimeout,
    isEditing,
    editedTaskRef,
    saveAllowed,
    setIsEditing,
    setSelectedEditableTask,
    openTaskDetails,
    handleSave
  );

  // Handle clicks outside the row
  useOutsideClick(
    rowRef,
    createTagOpen,
    createDueDateOpen,
    createAssigneeOpen,
    saveAllowed,
    editedTaskRef,
    handleSave,
    setIsEditing
  );

  // #endregion Click Handling Edit/View

  // #region Task Handling
  const { duplicateTask } = useDuplicateTask({
    onTaskDuplicated: async (newTaskID) => {
      await forceFetchAll();
      nav(`${ROUTE_TASKS}/${newTaskID}`);
    },
  });

  const { deleteTask } = useDeleteTask({
    onTaskDeleted: async () => {
      removeTaskFromUserTasks(task.id);
      await forceFetchAll();
      if (window.location.pathname === `${ROUTE_TASKS}/${task.id}`) {
        nav(ROUTE_TASKS);
      }
    },
  });

  function openTaskDetails() {
    nav(`${ROUTE_TASKS}/${task.id}`);
  }

  // #endregion Task Handling

  return (
    <ContextMenu>
      <ContextMenuTrigger>
        <div
          className={`grid grid-cols-14 items-center py-1 hover:bg-accent hover:text-accent-foreground border-l-4 h-12 ${
            taskType === "Overdue"
              ? "border-red-300"
              : taskType === "Due Today"
                ? "border-green-300"
                : taskType === "Due Later"
                  ? "border-indigo-300"
                  : "border-gray-300"
          }`}
          ref={rowRef}
          onClick={() => {
            if (inlineEditingDisabled) {
              return;
            }
            handleSingleClick();
          }}
          onDoubleClick={async () => await handleDoubleClick()}
        >
          {/* Complete Task */}
          <div
            className={`flex items-center gap-2 col-span-1 ${
              onSelectChange ? "px-2" : ""
            }`}
          >
            {onSelectChange && (
              <Checkbox
                checked={isSelected}
                onCheckedChange={(value) => onSelectChange(!!value)}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                aria-label="Select task"
              />
            )}
            <TaskCellStatus
              className="flex items-center"
              completed={task.status === "complete"}
              taskID={task.id}
              completeTask={completeTask}
              iconClassName={onSelectChange ? "ms-0" : undefined}
            />
          </div>
          {/* Title */}
          <TaskCellTitle
            className={
              isEditing && editedTask
                ? "px-2 col-span-3"
                : "px-2 col-span-3 select-none"
            }
            editing={isEditing}
            editedTitle={editedTask ? editedTask.title : undefined}
            title={task.title}
            inputID="edit-title"
            handleEditField={handleEditField}
          />
          {/* Assignee */}
          <TaskCellAssignee
            className={
              isEditing && editedTask
                ? "px-2 col-span-2"
                : "px-2 col-span-2 truncate select-none"
            }
            assignedTo={task.assignee_id}
            editedAssignedTo={editedTask?.assigned_to}
            editing={isEditing}
            handleFieldAssignedTo={handleFieldAssignedTo}
            isOpen={createAssigneeOpen}
          />

          {/* Tags */}
          <TaskCellTags
            className={
              isEditing && editedTask
                ? "px-2 col-span-4"
                : "px-2 col-span-4 w-full truncate select-none"
            }
            editing={isEditing}
            taskTags={task.tags}
            editedTags={editedTask?.tags || undefined}
            selectedEditableTaskTags={selectedEditableTask?.tags || undefined}
            handleFieldTags={handleFieldTags}
            createTagOpen={createTagOpen}
          />

          {/* Due Date */}
          <TaskCellDueDate
            className={
              isEditing && editedTask
                ? "px-2 col-span-2"
                : "px-2 col-span-2 select-none"
            }
            dueDate={task.due_date}
            editedDueDate={editedTask?.due_date || undefined}
            editing={isEditing}
            handleFieldDueDate={handleFieldDueDate}
            createDueDateOpen={createDueDateOpen}
          />

          {/* Timer */}
          <div className="px-2 col-span-2 text-right">
            <TaskTimer
              type={{
                location: "allTasks",
                taskName: task.title,
                taskID: task.id,
              }}
            />
          </div>
        </div>
      </ContextMenuTrigger>
      {/* Right Click Menu */}
      <ContextMenuContent className="outline outline-1 outline-gray-300 dark:outline-gray-700">
        <ContextMenuItem
          className="cursor-pointer"
          onClick={() => {
            openTaskDetails();
          }}
        >
          Open
        </ContextMenuItem>
        <ContextMenuItem
          className="cursor-pointer"
          onClick={() => {
            setIsCopyDialogOpen(true);
          }}
        >
          Duplicate Task
        </ContextMenuItem>
        <ContextMenuItem
          className="text-red-500 cursor-pointer"
          onClick={() => {
            setIsDeleteDialogOpen(true);
          }}
        >
          Delete
        </ContextMenuItem>
      </ContextMenuContent>

      {/* Copy Alert Dialog */}
      <DuplicateTaskDialog
        isOpen={isCopyDialogOpen}
        onClose={() => setIsCopyDialogOpen(false)}
        onDuplicate={async () => {
          await duplicateTask(task.id);
          setIsCopyDialogOpen(false);
        }}
      />

      {/* Delete Task Alert Dialog */}
      <DeleteTaskDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onDelete={async () => {
          await deleteTask(task.id);
          setIsDeleteDialogOpen(false);
        }}
        type="single"
      />
    </ContextMenu>
  );
};

export default SingleLineTask;
